<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Policy from 'dashboard/components/policy.vue';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  sku: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  displayPrice: {
    type: String,
    default: '',
  },
  enabled: {
    type: Boolean,
    default: true,
  },
  customFields: {
    type: Object,
    default: () => ({}),
  },
  media: {
    type: Array,
    default: () => [],
  },
  updatedAt: {
    type: Number,
    required: true,
  },
  createdAt: {
    type: Number,
    required: true,
  },
  indexing: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['action']);

const { t } = useI18n();

const [showActionsDropdown, toggleDropdown] = useToggle();

const indexingStatus = computed(() => props.indexing?.status);
const isPending = computed(() => indexingStatus.value === 'pending');
const hasFailed = computed(() => indexingStatus.value === 'failed');

const menuItems = computed(() => [
  // Reindexing a disabled item would put back the very row that turning it off removed, so
  // the option is only there when it can do something.
  ...(props.enabled && !isPending.value
    ? [
        {
          label: t('CAPTAIN.CATALOG_ITEMS.OPTIONS.REINDEX_ITEM'),
          value: 'reindex',
          action: 'reindex',
          icon: 'i-lucide-refresh-cw',
        },
      ]
    : []),
  {
    label: t('CAPTAIN.CATALOG_ITEMS.OPTIONS.EDIT_ITEM'),
    value: 'edit',
    action: 'edit',
    icon: 'i-lucide-pencil-line',
  },
  {
    label: t('CAPTAIN.CATALOG_ITEMS.OPTIONS.DELETE_ITEM'),
    value: 'delete',
    action: 'delete',
    icon: 'i-lucide-trash',
  },
]);

const timestamp = computed(() =>
  dynamicTime(props.updatedAt || props.createdAt)
);

const extraFieldsCount = computed(
  () => Object.keys(props.customFields || {}).length
);

// The first photo, if there is one: it is what tells two similar items apart at a glance.
const thumbnail = computed(() =>
  (props.media || []).find(file =>
    String(file.content_type).startsWith('image/')
  )
);

const mediaCount = computed(() => (props.media || []).length);

// Whether the agent can find this item at all -- a disabled one is out of the index by
// design, so it says that instead of an indexing state it does not have.
const searchState = computed(() => {
  if (!props.enabled) {
    return {
      label: t('CAPTAIN.CATALOG_ITEMS.STATE.DISABLED'),
      icon: 'i-lucide-eye-off',
      tone: 'muted',
    };
  }

  if (isPending.value) {
    return {
      label: t('CAPTAIN.CATALOG_ITEMS.STATE.PENDING'),
      icon: 'i-lucide-loader-2 animate-spin',
      tone: 'muted',
    };
  }

  if (hasFailed.value) {
    return {
      label: t('CAPTAIN.CATALOG_ITEMS.STATE.FAILED'),
      icon: 'i-lucide-triangle-alert',
      tone: 'error',
    };
  }

  return {
    label: t('CAPTAIN.CATALOG_ITEMS.STATE.INDEXED', {
      time: dynamicTime(props.indexing?.indexed_at || props.updatedAt),
    }),
    icon: 'i-lucide-database',
    tone: 'muted',
  };
});

const handleAction = ({ action, value }) => {
  toggleDropdown(false);
  emit('action', { action, value, id: props.id });
};
</script>

<template>
  <CardLayout class="relative" :class="{ 'opacity-60': !enabled }">
    <div class="flex w-full min-w-0 gap-3">
      <img
        v-if="thumbnail"
        :src="thumbnail.url"
        :alt="name"
        class="object-cover rounded-lg size-14 shrink-0 bg-n-alpha-2"
      />

      <div class="flex flex-col flex-1 min-w-0 gap-2">
        <div class="relative flex justify-between w-full min-w-0 gap-1">
          <span class="min-w-0 font-medium truncate text-base text-n-slate-12">
            {{ name }}
          </span>
          <div class="flex items-center gap-2 shrink-0">
            <span
              v-if="displayPrice"
              class="text-base font-medium text-n-slate-12"
            >
              {{ displayPrice }}
            </span>
            <Policy
              v-on-clickaway="() => toggleDropdown(false)"
              :permissions="['administrator']"
              class="relative flex items-center group"
            >
              <Button
                icon="i-lucide-ellipsis-vertical"
                color="slate"
                size="xs"
                class="rounded-md group-hover:bg-n-alpha-2"
                @click="toggleDropdown()"
              />
              <DropdownMenu
                v-if="showActionsDropdown"
                :menu-items="menuItems"
                class="mt-1 ltr:right-0 rtl:right-0 top-full"
                @action="handleAction($event)"
              />
            </Policy>
          </div>
        </div>

        <div class="flex items-center justify-between w-full min-w-0 gap-4">
          <div class="flex items-center flex-1 min-w-0 gap-3">
            <span
              v-if="sku"
              class="inline-flex items-center gap-1 text-sm shrink-0 text-n-slate-11"
            >
              <i class="text-base i-lucide-hash" />
              {{ sku }}
            </span>
            <span
              v-if="description"
              class="flex-1 min-w-0 text-sm truncate text-n-slate-11"
              :title="description"
            >
              {{ description }}
            </span>
          </div>
          <span class="text-sm text-n-slate-11 line-clamp-1 shrink-0">
            {{ timestamp }}
          </span>
        </div>

        <div class="flex flex-col w-full min-w-0 gap-1.5">
          <div
            class="flex items-center min-w-0 gap-2 text-sm"
            :class="
              searchState.tone === 'error'
                ? 'text-n-ruby-11'
                : 'text-n-slate-11'
            "
          >
            <span :class="searchState.icon" class="text-base shrink-0" />
            <span class="min-w-0 truncate">{{ searchState.label }}</span>
            <span
              v-if="mediaCount"
              class="flex items-center gap-2 shrink-0 text-n-slate-11"
            >
              <span class="w-1 h-1 rounded-full bg-n-slate-8 shrink-0" />
              {{
                $t('CAPTAIN.CATALOG_ITEMS.STATE.MEDIA_COUNT', {
                  count: mediaCount,
                })
              }}
            </span>
            <span
              v-if="extraFieldsCount"
              class="flex items-center gap-2 shrink-0 text-n-slate-11"
            >
              <span class="w-1 h-1 rounded-full bg-n-slate-8 shrink-0" />
              {{
                $t('CAPTAIN.CATALOG_ITEMS.STATE.EXTRA_FIELDS', {
                  count: extraFieldsCount,
                })
              }}
            </span>
          </div>
          <span
            v-if="hasFailed && indexing.error"
            class="text-sm break-words text-n-slate-11"
          >
            {{ indexing.error }}
          </span>
        </div>
      </div>
    </div>
  </CardLayout>
</template>
